import React, { HTMLAttributes, useCallback, useMemo, useState } from "react";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Popper,
  TextField,
} from "@mui/material";

import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";

import MobileSelect from "./MobileSelect";

interface Option {
  label: string;
  value: string;
}

interface ProgramsProps {
  title: string;
  options: Option[];
  selectOptions: Option[];
  onChange: (data: Option[]) => void;
  size?: "small" | "medium";
  placeholder?: string;
}

const Programs = ({ title, options, size, selectOptions, placeholder, onChange }: ProgramsProps) => {
  const { isMobile } = usePlatform();
  const [open, setOpen] = useState(false);

  const handleChange = useEvent((e: React.SyntheticEvent, data: Array<Option | string>) => {
    onChange(data as Option[]);
  });

  const renderTags = useCallback(
    (value: Option[], getTagProps: AutocompleteRenderGetTagProps) =>
      value.map((option, index) => (
        <Chip
          label={option.label}
          {...getTagProps({ index })}
          key={option.value}
          sx={{ "& .MuiChip-label": { paddingRight: "8px" } }}
          deleteIcon={
            <IconButton size="small" sx={{ width: "21px", height: "21px" }}>
              <CloseIcon className="!text-[16px]" />
            </IconButton>
          }
        />
      )),
    []
  );

  const renderOption = useCallback(
    (props: HTMLAttributes<HTMLElement>, option: Option) => {
      const selected = selectOptions.findIndex((ele) => ele.value === option.value) !== -1;

      return (
        <li
          {...props}
          style={{ padding: "4px", paddingLeft: "6px" }}
          key={option.value}
          onClick={(e) => {
            e.stopPropagation();

            let nextOptions = [];

            if (selected) {
              nextOptions = selectOptions.filter((ele) => ele.value !== option.value);
            } else {
              nextOptions = [...selectOptions, option].filter((ele) => ele.value);
            }

            onChange(nextOptions);
          }}
        >
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon />}
            checkedIcon={<CheckBoxIcon />}
            checked={selected}
          />

          <div className="pl-[4px]">
            <div className="flex text-sm">{option.label}</div>
          </div>
        </li>
      );
    },
    [selectOptions, onChange]
  );

  const mobileProps = useMemo(() => {
    return isMobile
      ? {
          PaperComponent: () => <div className="hidden" />,
          renderOption: () => null,
          sx: {
            "& .MuiInputBase-root": {
              minWidth: "150px",
              paddingRight: "25px !important",
            },
            "& .MuiAutocomplete-popupIndicator": {
              transform: "rotate(0deg) !important",
            },
            "& .MuiInputBase-input": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              visibility: "hidden",
              width: "1px",
              minWidth: "1px !important",
            },
            "& .MuiAutocomplete-popper": {
              display: "none",
            },
            "& .MuiChip-root": {
              maxWidth: "135px",
              minWidth: "46px",
            },
            "& .MuiChip-deleteIcon": {
              display: "none",
            },
            "& .MuiChip-label": {
              padding: "0 12px !important",
            },
          },
        }
      : {};
  }, [isMobile]);

  return (
    <>
      <div
        className="max-w-full min-w-full md:min-w-[300px]"
        onClick={(e) => {
          e.stopPropagation();

          if (isMobile) {
            setOpen(true);
          }
        }}
      >
        <Autocomplete
          multiple
          size={size}
          freeSolo
          clearOnBlur
          forcePopupIcon
          options={options}
          limitTags={isMobile ? 1 : 100}
          disableCloseOnSelect
          value={selectOptions}
          renderTags={renderTags}
          onChange={handleChange}
          style={{ width: "100%" }}
          renderOption={renderOption}
          renderInput={(params) => (
            <TextField {...params} fullWidth size={size} placeholder={placeholder} />
          )}
          PaperComponent={(props: HTMLAttributes<HTMLElement>) => (
            <Paper {...props} elevation={8} style={{ minWidth: 300 }}>
              {props.children}
            </Paper>
          )}
          PopperComponent={(props) => <Popper {...props} placement="bottom-start" />}
          {...mobileProps}
        />
      </div>
      <MobileSelect
        options={options}
        selectOptions={selectOptions}
        title={title}
        open={open}
        onOpen={(val) => {
          setOpen(val);
        }}
        onChange={(data) => {
          onChange(data);
        }}
      />
    </>
  );
};

export default React.memo(Programs);
